/**
 * productCoverage: one place that knows how a catalog product converts
 * square feet into units (bags / sets / bundles), for the quote line.
 *
 * IDI-173 (+ IDI-430 link 3). Legacy BIDIT shows a separate "Coverage"
 * dropdown after Product for every product with a coverage chart. 2.0 baked
 * the R-value into the product name, with no way to choose. Now:
 *
 *   blow-in    -> options = the R-values with a coverage in the product's
 *                 attic chart (or wall chart, by the sub-area's surface);
 *                 sqftPerUnit = that chart value (sq ft per BAG)
 *   spray foam -> options = inch thicknesses up to max build;
 *                 sqftPerUnit = per-set sq ft at 1" / inches (sq ft per SET)
 *   batts etc. -> no dropdown; sqftPerUnit = the product's sq ft per unit
 *
 * The coverage is the RESOLVED one from listPricebook (dealer's own -> IDI
 * central -> none, IDI-338), carried on the product by synthesizeTenantProduct.
 */

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// "R-38HP" -> 38.5 so HP sorts just after R-38.
const rKey = (label) => {
  const m = String(label).toUpperCase().match(/R-?(\d+(?:\.\d+)?)(.*)$/);
  return m ? Number(m[1]) + (m[2] ? 0.5 : 0) : Number.POSITIVE_INFINITY;
};

/**
 * The surface a sub-area insulates, for picking the attic vs wall chart.
 * Walls / rim / band / knee-wall are "wall"; everything else (attics,
 * ceilings, cathedral, floors blown from above) is "attic".
 */
export function surfaceForSubArea(areaId, subAreaLabel) {
  const s = `${areaId || ''} ${subAreaLabel || ''}`.toLowerCase();
  return /wall|rim|band|knee/.test(s) ? 'wall' : 'attic';
}

/**
 * Chart the line should read for this product + surface.
 *
 * IDI-434 (RULED, Adrian 2026-09-30): an attic chart never stands in for a
 * wall chart, or the reverse. A product with an attic chart and no wall chart
 * is NOT offered on a wall sub-area. The single unsplit chart
 * (`coverageRValues`, no surface) applies to either surface, but only when the
 * product has no surface-specific chart at all.
 */
function chartFor(product, surface) {
  const attic = product?.coverageRValuesAttic || null;
  const wall = product?.coverageRValuesWall || null;
  const single = product?.coverageRValues || null;
  const pick = (m) => (m && Object.values(m).some((v) => num(v) > 0) ? m : null);
  if (pick(attic) || pick(wall)) return surface === 'wall' ? pick(wall) : pick(attic);
  return pick(single);
}

/**
 * IDI-434: can this product be priced by area on this surface? Spray foam
 * (per set) and a plain sq ft per unit apply to any surface; an R-value
 * chart only to its own surface (see chartFor).
 */
export function isOfferableOnSurface(product, surface = 'attic') {
  if (!product) return false;
  const isChart = product.coverageMode === 'rvalue-attic-wall' || product.coverageMode === 'rvalue';
  if (!isChart) return true;
  return chartFor(product, surface) !== null;
}

/**
 * Coverage options for the dropdown, or [] when the product has none to
 * choose (batts, generic per-unit, or no coverage at all).
 * Each option: { value, label, sqftPerUnit, unitLabel }.
 */
// IDI-507 (9): the spray-foam max thickness when the product has none saved.
// The Item Catalog editor shows 14.5" by default, so the builder must offer the
// same ladder (it capped at 6", so a quote couldn't pick what the editor showed).
export const DEFAULT_SPRAYFOAM_MAX_THICKNESS_IN = 14.5;

export function coverageOptions(product, surface = 'attic') {
  if (!product) return [];
  if (product.coverageMode === 'sprayfoam' && num(product.coveragePerSet) > 0) {
    const perSet = num(product.coveragePerSet);
    const max = num(product.coverageMaxThicknessIn) > 0 ? num(product.coverageMaxThicknessIn) : DEFAULT_SPRAYFOAM_MAX_THICKNESS_IN;
    const out = [];
    for (let inches = 1; inches <= max + 1e-9; inches += 0.5) {
      out.push({ value: `${inches}in`, label: `${inches}"`, sqftPerUnit: perSet / inches, unitLabel: 'set', inches });
    }
    return out;
  }
  const chart = chartFor(product, surface);
  if (!chart) return [];
  return Object.entries(chart)
    .filter(([, v]) => num(v) > 0)
    .sort(([a], [b]) => rKey(a) - rKey(b))
    .map(([r, v]) => ({ value: r, label: r, sqftPerUnit: num(v), unitLabel: 'bag', rValue: r }));
}

/** True when the product needs a Coverage choice before it can be priced. */
export function needsCoverageChoice(product, surface = 'attic') {
  return coverageOptions(product, surface).length > 0;
}

/** Units to buy for `sqft`: bags and bundles round UP; spray sets are exact to 2 dp. */
export function unitsFor(sqft, sqftPerUnit, unitLabel) {
  if (!(sqftPerUnit > 0) || !(sqft > 0)) return 0;
  const raw = sqft / sqftPerUnit;
  return unitLabel === 'set' ? Math.round(raw * 100) / 100 : Math.ceil(raw - 1e-9);
}
